import React, {useState, useEffect, use, createContext} from "react";
import PropTypes from "prop-types";
import {useLocation} from "react-router-dom";
import {useMediaQuery, useTheme} from "@mui/material";
import {useLazyQuery, useQuery, useSubscription} from "@apollo/client";
import {GET_ACTION_COUNT_FOR_USER} from "../../../graphql/actionQueries";
import {
  GET_UNREAD_MESSAGE_COUNT,
  SUBSCRIBE_TO_USER_MESSAGES,
} from "../../../graphql/messageQueries";
import {GET_ACTION_PHASES} from "../../../graphql/phaseQueries";
import {ALERT} from "../../../resources/environment";
import {handleSubscriptionError} from "../../../resources/utils/error";
import {useUser} from "../../user/Context/UserProvider";
import {useFeatureFlags} from "../../../context/FeatureFlagProvider";
import {includes, isEmpty, max, without} from "underscore";

export const SidebarContext = createContext(null);

export const SidebarProvider = ({children}) => {
  const {user} = useUser();
  const {newSidebarDesign} = useFeatureFlags();
  const location = useLocation();
  const theme = useTheme();
  const xsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const printQuery = useMediaQuery("print");
  const [currentPage, setCurrentPage] = useState();
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);
  const [showSidebarText, setShowSidebarText] = useState(false);
  const openMenusOnStart = ["Respond"];
  const [sidebarMenuOpen, setSidebarMenuOpen] = useState(openMenusOnStart);

  const {data: subscribedMessageData, error: subscribedMessageError} = useSubscription(
    SUBSCRIBE_TO_USER_MESSAGES,
    {
      variables: {
        userId: parseInt(user.id, 10),
      },
    },
  );

  const {
    data: unreadData,
    refetch: refetchMessage,
    error: unreadError,
  } = useQuery(GET_UNREAD_MESSAGE_COUNT, {
    variables: {userId: user.id},
  });

  const {data: actionPhases, error: actionPhasesError} = useQuery(GET_ACTION_PHASES);

  const [
    getMyActionCount,
    {
      data: myActionCount,
      loading: loadingMyActionCount,
      refetch: refetchMyActionCount,
      error: myActionCountError,
    },
  ] = useLazyQuery(GET_ACTION_COUNT_FOR_USER);

  useEffect(() => {
    if (subscribedMessageError) {
      handleSubscriptionError(subscribedMessageError, "User messages");
    }
  }, [subscribedMessageError]);

  useEffect(() => {
    if (unreadError) {
      ALERT.error("Could not get Message data, please try again.");
    }
  }, [unreadError]);

  useEffect(() => {
    if (actionPhasesError) {
      console.error(actionPhasesError);
    }
  }, [actionPhasesError]);

  useEffect(() => {
    if (myActionCountError) {
      console.error(myActionCountError);
    }
  }, [myActionCountError]);

  useEffect(() => {
    const path = location.pathname;
    const page = `/${path.split("/")[1]}`;
    setCurrentPage(page);
    if (!newSidebarDesign) {
      if (user?.wantsSidebarClosed) setIsSidebarHidden(true);
      else setIsSidebarHidden(false);
    }
  }, []);

  useEffect(() => {
    if (newSidebarDesign && xsScreen) {
      setIsSidebarHidden(true);
      setShowSidebarText(false);
    } else if (newSidebarDesign) {
      setIsSidebarHidden(false);
    }
  }, [newSidebarDesign, xsScreen]);

  const setMenuOpenIfNeeded = (menu, paths, page) => {
    if (includes(paths, page) && !includes(sidebarMenuOpen, menu)) {
      setSidebarMenuOpen((current) => [...current, menu]);
    }
  };

  const whichSidebarEntryToHighlight = (page, subpage) => {
    if (["reports"].includes(page)) return `${page}/${subpage}`;
    if (["compliance"].includes(page)) {
      if (!["summary", "plan", "research"].includes(subpage)) return "compliance/summary";
      return `${page}/${subpage}`;
    }
    return page;
  };

  useEffect(() => {
    const path = location.pathname;
    const page = `${path.split("/")[1]}`;
    const subpage = `${path.split("/")?.[2]}`;
    if (!newSidebarDesign) {
      setMenuOpenIfNeeded("Respond", ["actions", "incidents"], page);
      setMenuOpenIfNeeded("Compliance", ["compliance"], page);
      setMenuOpenIfNeeded("Report", ["reports"], page);
      setMenuOpenIfNeeded(
        "Sources",
        ["obligations", "tasks", "playbooks", "datasets", "data"],
        page,
      );
      setMenuOpenIfNeeded("Settings", ["users", "roles", "organization", "notifications"], page);
      setMenuOpenIfNeeded("Integrations", ["integrations", "api"], page);
    }
    setCurrentPage(whichSidebarEntryToHighlight(page, subpage));
  }, [location.pathname]);

  useEffect(() => {
    if (xsScreen && !printQuery) setIsSidebarHidden(true);
  }, [xsScreen, printQuery]);

  useEffect(() => {
    refetchMessage();
  }, [subscribedMessageData]);

  useEffect(() => {
    if (!isEmpty(actionPhases) && !loadingMyActionCount) {
      const lastActionPhase = max(actionPhases.actionPhases, (actionPhase) => actionPhase.ordering);
      getMyActionCount({
        variables: {userId: user.id, lastActionPhase: lastActionPhase.id},
      });
    }
  }, [actionPhases]);

  const toggleSidebar = () => {
    if (newSidebarDesign) {
      if (xsScreen) {
        setIsSidebarHidden(!isSidebarHidden);
      } else {
        setShowSidebarText(!showSidebarText);
      }
    } else {
      setIsSidebarHidden(!isSidebarHidden);
    }
  };

  const toggleSidebarMenu = (item) => {
    const isItemOpen = includes(sidebarMenuOpen, item);
    if (isItemOpen) setSidebarMenuOpen((current) => without(current, item));
    else setSidebarMenuOpen((current) => [...current, item]);
  };

  return (
    <SidebarContext
      value={{
        // state
        currentPage,
        setCurrentPage,
        isSidebarHidden,
        setIsSidebarHidden,
        sidebarMenuOpen,
        setSidebarMenuOpen,
        xsScreen,
        showSidebarText,

        // queries
        myActionCount,
        unreadData,

        // functions
        toggleSidebar,
        toggleSidebarMenu,
        refetchMyActionCount,
        refetchMessage,
      }}
    >
      {children}
    </SidebarContext>
  );
};

SidebarProvider.propTypes = {
  children: PropTypes.any,
};

export const useSidebar = () => {
  return use(SidebarContext);
};
